"use client";

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import IntegrationConfirmDialog from "@/components/integrations/marketplace/IntegrationConfirmDialog";
import Icon from "@/components/library/Icon";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { MetaCredentialsController } from "@/hooks/useMetaCredentials";
import type { NodeProviderCredential } from "@/types/workflows";

interface MetaSetupPanelProps {
  /** Owned by the card, which needs the same rows for its badge. */
  meta: MetaCredentialsController;
}

/**
 * Always an absolute date, never "Today": the copy interpolates into a
 * preposition ("Conectada el {{date}}"), and a relative label reads as "el Hoy".
 * `locale` is the i18n language, not the browser's — a Japanese UI said en-US.
 *
 * `null` means the line is omitted entirely rather than rendered half-built: an
 * empty `createdAt` used to print "Connected " with a dangling space, and an
 * unparseable one echoed Omega's raw value into the UI.
 */
function connectedOnLabel(createdAt: string, locale: string): string | null {
  const parsed = new Date(createdAt);
  if (!createdAt || Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Dialog body for the Meta tile. Connect stays available while Pages are
 * connected: it doubles as "reconnect an expired grant", which lands on the same
 * credential row rather than duplicating it.
 */
export default function MetaSetupPanel({ meta }: Readonly<MetaSetupPanelProps>) {
  const { t, i18n } = useTranslation("partners");
  const { t: ct } = useTranslation("common");
  const { status, credentials, isConnecting, disconnectingId, load, connect, disconnect } = meta;
  const [pendingDisconnect, setPendingDisconnect] = useState<NodeProviderCredential | null>(null);

  const connectedOn = useCallback(
    (createdAt: string) => connectedOnLabel(createdAt, i18n.language),
    [i18n.language]
  );

  // The dialog stays up for the call so a second confirm cannot be fired at a
  // Page already being removed. Both outcomes are reported by a toast.
  const handleConfirmDisconnect = useCallback(async () => {
    if (!pendingDisconnect) return;
    await disconnect(pendingDisconnect.id, pendingDisconnect.label);
    setPendingDisconnect(null);
  }, [disconnect, pendingDisconnect]);

  if (status === "loading") {
    return (
      <div className="space-y-3" data-testid="meta-setup-skeleton">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  // Distinct from the empty state on purpose: "no Page is connected yet" over a
  // failed read would send the customer to connect a Page they already have.
  if (status === "loadError") {
    return (
      <div className="space-y-3" data-testid="meta-setup-load-error">
        <p className="text-sm text-500">{t("metaIntegration.loadError")}</p>
        <Button type="button" size="sm" variant="outline" onClick={load}>
          {ct("retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="meta-setup-panel">
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-semibold">{t("metaIntegration.pagesHeading")}</h4>
        {credentials.length === 0 ? (
          <p className="text-sm text-500" data-testid="meta-setup-empty">
            {t("metaIntegration.empty")}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {credentials.map((credential) => {
              const connectedOnDate = connectedOn(credential.createdAt);
              return (
              <li
                key={credential.id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
                data-testid={`meta-page-row-${credential.id}`}
              >
                <div className="flex flex-1 flex-col">
                  {/* Meta-controlled text: rendered as JSX text, never as markup. */}
                  <span className="text-sm font-medium">{credential.label}</span>
                  {connectedOnDate && (
                    <span className="text-xs text-500">
                      {t("metaIntegration.connectedOn", { date: connectedOnDate })}
                    </span>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPendingDisconnect(credential)}
                  disabled={disconnectingId === credential.id}
                  data-testid={`meta-disconnect-${credential.id}`}
                >
                  {ct("disconnect")}
                </Button>
              </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Button
          type="button"
          size="sm"
          className="self-start"
          onClick={connect}
          disabled={isConnecting}
          data-testid="meta-connect-page"
        >
          <Icon icon="plus" />
          {isConnecting ? t("metaIntegration.connecting") : t("metaIntegration.connectPage")}
        </Button>
        <p className="text-xs text-500">{t("metaIntegration.workflowRelationship")}</p>
      </div>

      <IntegrationConfirmDialog
        open={pendingDisconnect !== null}
        onOpenChange={(next) => !next && setPendingDisconnect(null)}
        title={t("metaIntegration.disconnectConfirmTitle", {
          name: pendingDisconnect?.label ?? "",
        })}
        body={t("metaIntegration.disconnectConfirmBody")}
        confirmLabel={ct("disconnect")}
        cancelLabel={ct("cancel")}
        onConfirm={handleConfirmDisconnect}
        busy={disconnectingId !== null}
        testId="meta-disconnect-confirm"
      />
    </div>
  );
}
